'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import { calcularFluxoCaixa } from '@/lib/calculos/fluxo-caixa'
import {
  AvisoBox,
  brl,
  DetailRow,
  LeadCapture,
  SalvarCalculoCta,
  Eyebrow,
  type ItemLista,
  ListaItens,
  MobileResultBar,
  MoneyField,
  parseValor,
  ResultCard,
  RowStrong,
  SoftExportBar,
  useAnimatedNumber,
  useCalcTelemetry,
} from '@/components/calc'
import type { CartaoPayload } from '@/lib/export/types'

export function FluxoClient() {
  const [saldoInicial, setSaldoInicial] = useState('')
  const [entradas, setEntradas] = useState<ItemLista[]>([{ id: 1, desc: '', valor: '' }])
  const [saidas, setSaidas] = useState<ItemLista[]>([{ id: 1, desc: '', valor: '' }])

  // Pré-preenche com o que entrou/saiu vindo do console da home (?entrou=&saiu=).
  useEffect(() => {
    const p = new URLSearchParams(window.location.search)
    const entrou = p.get('entrou')
    const saiu = p.get('saiu')
    if (entrou) setEntradas([{ id: 1, desc: 'Entrada', valor: entrou }])
    if (saiu) setSaidas([{ id: 1, desc: 'Saída', valor: saiu }])
  }, [])
  const entId = useRef(2)
  const saiId = useRef(2)

  const addEntrada = (desc = '') => setEntradas((l) => [...l, { id: entId.current++, desc, valor: '' }])
  const removeEntrada = (id: number) => setEntradas((l) => l.filter((c) => c.id !== id))
  const updateEntrada = (id: number, f: 'desc' | 'valor', v: string) =>
    setEntradas((l) => l.map((c) => (c.id === id ? { ...c, [f]: v } : c)))

  const addSaida = (desc = '') => setSaidas((l) => [...l, { id: saiId.current++, desc, valor: '' }])
  const removeSaida = (id: number) => setSaidas((l) => l.filter((c) => c.id !== id))
  const updateSaida = (id: number, f: 'desc' | 'valor', v: string) =>
    setSaidas((l) => l.map((c) => (c.id === id ? { ...c, [f]: v } : c)))

  const totalEntradas = useMemo(() => entradas.reduce((s, c) => s + parseValor(c.valor), 0), [entradas])
  const totalSaidas = useMemo(() => saidas.reduce((s, c) => s + parseValor(c.valor), 0), [saidas])

  const r = useMemo(
    () => calcularFluxoCaixa({ saldoInicial: parseValor(saldoInicial), totalEntradas, totalSaidas }),
    [saldoInicial, totalEntradas, totalSaidas]
  )

  const saldoAnim = useAnimatedNumber(r.saldoFinal)
  const negativo = r.status === 'negativo'

  /** Guarda da barra mobile: sem isto ela nascia fixa no rodapé mostrando R$ 0,00. */
  const temResultado = parseValor(saldoInicial) !== 0 || totalEntradas > 0 || totalSaidas > 0

  useCalcTelemetry(
    'fluxo_caixa',
    parseValor(saldoInicial) !== 0 || totalEntradas > 0 || totalSaidas > 0,
    `Caixa: ${r.saldoFinal < 0 ? '− ' : ''}${brl(Math.abs(r.saldoFinal))}`,
  )

  const buildCartao = (): CartaoPayload => ({
    nome: 'fluxo-de-caixa',
    heroLabel: 'Seu caixa',
    heroValue: `${r.saldoFinal < 0 ? '− ' : ''}${brl(Math.abs(r.saldoFinal))}`,
    tone: negativo ? 'neg' : 'brand',
    linhas: [
      { label: 'Saldo inicial', value: brl(r.saldoInicial) },
      { label: 'Entradas', value: brl(r.totalEntradas) },
      { label: 'Saídas', value: brl(r.totalSaidas) },
      {
        label: r.resultado >= 0 ? 'No período, entrou a mais' : 'No período, saiu a mais',
        value: `${r.resultado < 0 ? '− ' : '+ '}${brl(Math.abs(r.resultado))}`,
        strong: true,
        tone: r.resultado >= 0 ? 'brand' : 'neg',
      },
    ],
  })

  const cardRef = useRef<HTMLElement>(null)

  return (
    <div className="grid grid-cols-1 gap-[18px] pb-24 lg:grid-cols-2 lg:items-start lg:pb-0">
      {/* ── INPUTS ── */}
      <section className="rounded-2xl border border-line bg-surface p-6 shadow-soft">
        <MoneyField
          label="Quanto você tinha no começo do período?"
          value={saldoInicial}
          onChange={setSaldoInicial}
          hint="O saldo de caixa antes das entradas e saídas abaixo."
        />
        <ListaItens
          label="Entradas (o que entrou)"
          items={entradas}
          onAdd={addEntrada}
          onRemove={removeEntrada}
          onUpdate={updateEntrada}
          sugestoes={['Vendas', 'Serviços', 'Recebimentos']}
          descPlaceholder="Ex.: Vendas"
          addLabel="Adicionar entrada"
        />
        <ListaItens
          label="Saídas (o que saiu)"
          items={saidas}
          onAdd={addSaida}
          onRemove={removeSaida}
          onUpdate={updateSaida}
          sugestoes={['Fornecedor', 'Aluguel', 'DAS (imposto)', 'Energia', 'Pró-labore']}
          descPlaceholder="Ex.: Fornecedor"
          addLabel="Adicionar saída"
        />
      </section>

      {/* ── RESULTADO ── */}
      <section ref={cardRef} className="flex flex-col rounded-2xl border border-line bg-bg-alt p-[22px] shadow-soft">
        <Eyebrow icon="wallet" tone="muted" className="mb-3">
          Seu fluxo de caixa
        </Eyebrow>

        <ResultCard
          tone={negativo ? 'neg' : 'brand'}
          heroLabel="Seu caixa"
          heroValue={`${r.saldoFinal < 0 ? '− ' : ''}${brl(Math.abs(saldoAnim))}`}
        >
          <DetailRow label="Saldo inicial" value={brl(r.saldoInicial)} />
          <DetailRow label="Entradas" value={brl(r.totalEntradas)} />
          <DetailRow label="Saídas" value={brl(r.totalSaidas)} />
          <RowStrong
            label={r.resultado >= 0 ? 'No período, entrou a mais' : 'No período, saiu a mais'}
            value={`${r.resultado < 0 ? '− ' : '+ '}${brl(Math.abs(r.resultado))}`}
            tone={r.resultado >= 0 ? 'brand' : 'neg'}
          />
        </ResultCard>

        {negativo && (
          <AvisoBox tone="neg" icon="alert" className="mt-4">
            Seu caixa fechou negativo — saiu mais do que entrou neste período. Vale revisar as saídas
            ou reforçar as entradas antes que o buraco aumente.
          </AvisoBox>
        )}

        <SoftExportBar buildCartao={buildCartao} />
        <LeadCapture show={parseValor(saldoInicial) !== 0 || totalEntradas > 0 || totalSaidas > 0} source="fluxo-de-caixa" alertType="das" />
        <SalvarCalculoCta
          show={parseValor(saldoInicial) !== 0 || totalEntradas > 0 || totalSaidas > 0}
          source="fluxo-de-caixa"
          hook="Saldo na mão. No Premium você registra o mês e vê o orçamento."
          calcData={
            totalEntradas > 0 || totalSaidas > 0
              ? {
                  tipo: 'FLUXO_CAIXA',
                  inputs: {
                    transacoes: [
                      ...entradas
                        .filter((e) => parseValor(e.valor) > 0)
                        .map((e) => ({
                          descricao: e.desc || 'Entrada',
                          valor: parseValor(e.valor),
                          categoria: 'Geral',
                          tipo: 'ENTRADA' as const,
                          data: new Date().toISOString().slice(0, 10),
                        })),
                      ...saidas
                        .filter((s) => parseValor(s.valor) > 0)
                        .map((s) => ({
                          descricao: s.desc || 'Saída',
                          valor: parseValor(s.valor),
                          categoria: 'Geral',
                          tipo: 'SAIDA' as const,
                          data: new Date().toISOString().slice(0, 10),
                        })),
                    ],
                  },
                  resultado: {
                    totalEntradas: r.totalEntradas,
                    totalSaidas: r.totalSaidas,
                    saldo: r.resultado,
                    margemOperacional: r.totalEntradas > 0 ? (r.resultado / r.totalEntradas) * 100 : 0,
                    entradasPorCategoria: { Geral: r.totalEntradas },
                    saidasPorCategoria: { Geral: r.totalSaidas },
                    statusSaude: r.resultado > 0 ? 'POSITIVO' : r.resultado < 0 ? 'NEGATIVO' : 'NEUTRO',
                    mensagem:
                      r.resultado >= 0
                        ? 'Mês positivo: entrou mais do que saiu.'
                        : 'Mês negativo: saiu mais do que entrou.',
                  },
                  titulo: 'Fluxo de caixa',
                }
              : undefined
          }
        />
      </section>

      {temResultado && <MobileResultBar payload={buildCartao()} cardRef={cardRef} />}
    </div>
  )
}
